import * as React from "react";
import { style } from "glamor";
import throttle from "lodash/throttle";
import { IMoveableHOCProps, IMoveableState } from "../infinite-interface";
import { result } from "../infinite-utils/infinite-utils-result";
import { Item } from "constants/product-collection";
import { amphibiousGetIn, amphibiousGet } from "helpers/objUtils";

export const getFilteredDestinationIndex = (draggingIndex, destinationIndex, items, allItems) => {
    const itemOnLeft =
        destinationIndex !== 0 && destinationIndex - 1 !== draggingIndex
            ? amphibiousGet(items, destinationIndex - 1)
            : null;
    if (itemOnLeft) {
        const index = allItems.findIndex(
            (item) => amphibiousGetIn(item, Item.FIELDS.ID) === amphibiousGetIn(itemOnLeft, Item.FIELDS.ID),
        );
        destinationIndex = index + 1;
    } else {
        const itemOnRight =
            destinationIndex < items.size - 1 && destinationIndex + 1 !== draggingIndex
                ? amphibiousGet(items, destinationIndex + 1)
                : null;
        if (itemOnRight) {
            const index = allItems.findIndex(
                (item) => amphibiousGetIn(item, Item.FIELDS.ID) === amphibiousGetIn(itemOnRight, Item.FIELDS.ID),
            );
            destinationIndex = index - 1;
        }
    }
    return destinationIndex;
};

export class MoveableHOC extends React.PureComponent<IMoveableHOCProps, IMoveableState> {
    private hasBeenTouchedRecently = false;
    private mouseClickTimeout?: any;
    private throttledUpdate?: any;
    private currentDraggingDestination?: number;
    private draggingBoundingRect: ClientRect;
    private containerBoundingRect: ClientRect;
    private draggingElement?: HTMLElement;
    private placed = false;
    private clickId = "";
    private clickGroupId = "";
    // private touchStart;

    private dragStyle = style({
        position: "fixed",
        top: "0px",
        left: "0px",
        display: "none",
        zIndex: 9999,
    });

    constructor(props) {
        super(props);

        this.state = {
            isDragging: false,
        };
    }

    UNSAFE_componentWillMount() {
        this.props.api.on("afterRenderCells", this.handleAfterRenderCells);
        this.props.api.on("beforeRenderCell", this.handleBeforeRenderCell);
    }

    componentWillUnmount() {
        this.props.api.off("afterRenderCells", this.handleAfterRenderCells);
        this.props.api.off("beforeRenderCell", this.handleBeforeRenderCell);
        if (this.props.scrollingContainer) {
            this.props.scrollingContainer.removeEventListener("touchstart", this.handleTouchStart);
            this.props.scrollingContainer.removeEventListener("mousedown", this.handleMouseDown);
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (this.props.scrollingContainer !== nextProps.scrollingContainer) {
            if (this.props.scrollingContainer) {
                this.props.scrollingContainer.removeEventListener("touchstart", this.handleTouchStart);
                this.props.scrollingContainer.removeEventListener("mousedown", this.handleMouseDown);
            }
            if (nextProps.scrollingContainer) {
                nextProps.scrollingContainer.addEventListener("touchstart", this.handleTouchStart, {
                    passive: false,
                } as any);
                nextProps.scrollingContainer.addEventListener("mousedown", this.handleMouseDown, {
                    passive: false,
                } as any);
            }
        }
    }

    render() {
        const { cellRenderer, items, children } = this.props;
        const { isDragging, mousePositionOnPage, itemsWithoutDraggingItem, draggingItem, draggingId } = this.state;
        return (
            <React.Fragment>
                {children({
                    mousePositionOnPage,
                    moveableItems: isDragging ? itemsWithoutDraggingItem : items,
                    draggingId,
                })}
                <div className="draggingElment" ref={this.bindDraggingRef} {...this.dragStyle}>
                    {draggingItem ? cellRenderer({ dataIndex: -1, data: draggingItem, isSortable: true }) : null}
                </div>
            </React.Fragment>
        );
    }

    private bindDraggingRef = (ref) => {
        this.draggingElement = ref;
    };

    private autoScroll() {
        const direction = {
            y: 0,
        };
        const { scrollingContainer, containerHeight } = this.props;
        const { mousePositionOnPage, scrollTriggerDistance, scrollTriggerFastDistance } = this.state;
        const { top } = this.containerBoundingRect;
        let pace = 6;
        if (mousePositionOnPage[1] >= top + containerHeight - scrollTriggerDistance) {
            direction.y = 1; // Scroll Down
            if (mousePositionOnPage[1] >= top + containerHeight - scrollTriggerFastDistance) {
                pace = 10;
            }
        } else if (mousePositionOnPage[1] <= top + scrollTriggerDistance) {
            direction.y = -1; // Scroll Up
            if (mousePositionOnPage[1] <= top + scrollTriggerFastDistance) {
                pace = 10;
            }
        }

        if (direction.y) {
            scrollingContainer.scrollTop = scrollingContainer.scrollTop + pace * direction.y;
        }
    }

    // insert blank spot where hover is
    private handleBeforeRenderCell = ({ index, lastOrigIndex, cursor, groupIndex, isLastIteminGroup }) => {
        const { containerWidth } = this.props;
        const { isDragging, mousePositionInScrollContainer, dimensions, draggingGroupId } = this.state;
        /* The dragging destination should only be set if the dragging event is happening within
           the same group */
        if (isDragging && groupIndex == draggingGroupId) {
            const { height, width } = dimensions;
            const [left, top] = mousePositionInScrollContainer;
            if (!this.placed && top >= cursor.top && top <= cursor.top + height) {
                if (!width || (left >= cursor.left && left <= cursor.left + width)) {
                    this.placed = true;
                    this.currentDraggingDestination = index;
                    this.moveCursorForPlaceholder(cursor, width, height, containerWidth);
                    // This condition is required for last item in each group
                } else if (isLastIteminGroup && left > cursor.left) {
                    this.placed = true;
                    this.currentDraggingDestination = lastOrigIndex + 1;
                }
            }
        }
    };

    private handleAfterRenderCells = ({ cursor, items }) => {
        const { allItems } = this.props;
        const { isDragging, dimensions, mousePositionInScrollContainer } = this.state;
        if (isDragging) {
            this.autoScroll();
            const [_left, top] = mousePositionInScrollContainer;
            const { height } = dimensions;
            if (!this.placed && top + height >= cursor.top) {
                this.currentDraggingDestination = allItems.size;
            } else if (!this.placed) {
                this.currentDraggingDestination = undefined;
            }
            cursor.top = cursor.top + height;
        }
        this.placed = false;
    };

    private moveCursorForPlaceholder(cursor, width, height, containerWidth) {
        if (width) {
            // see if it will fit, if not bump it down
            if (cursor.left + width > containerWidth) {
                cursor.left = 0;
                cursor.top = cursor.top + height;
            }
            cursor.left = cursor.left + width;
        } else {
            cursor.top = cursor.top + height;
        }
    }

    private findElementByAttributeRecursively = (el, attribute) => {
        if (!el || !el.getAttribute) {
            return null;
        }
        if (el.getAttribute("data-stoppropagation")) {
            return null;
        } else if (el.getAttribute(attribute)) {
            return el;
        }

        if (!el.parentNode) {
            return null;
        }

        return this.findElementByAttributeRecursively(el.parentNode, attribute);
    };

    // triggered by drag handle
    private handleTouchStart = (e) => {
        if (!this.findElementByAttributeRecursively(e.target, "data-draghandle")) {
            return;
        }

        e.preventDefault();
        const target = this.findElementByAttributeRecursively(e.target, "data-id");
        if (target) {
            return;
        }

        const touch = e.touches[0];
        // this.touchStart = touch;
        this.handleCommonDown(touch, target);
    };

    private handleMouseDown = (e) => {
        if (e.button === 2) {
            return;
        }
        const target = this.findElementByAttributeRecursively(e.target, "data-id");
        this.handleCommonDown(e, target);
    };

    private handleCommonDown = (e, target) => {
        const { scrollingContainer } = this.props;

        if (!target || this.shouldCancelStart(e)) {
            return false;
        }

        // Hack for https://github.com/facebook/react/issues/9809
        if (this.hasBeenTouchedRecently) {
            return false;
        }

        // Dont allow sorting if sortable flag is false
        if (!this.props.sortable) {
            if (this.props.onClick) {
                this.props.onClick(target.getAttribute("data-id"));
            }
            return false;
        }

        if (e.persist) {
            e.persist();
            e.preventDefault();
        } else {
            // touch event
            this.hasBeenTouchedRecently = true;
            setTimeout(() => {
                this.hasBeenTouchedRecently = false;
            }, 500);
        }

        scrollingContainer.addEventListener("mouseup", this.handleMouseUp, { passive: true } as any);
        scrollingContainer.addEventListener("touchend", this.handleTouchEnd, { passive: true } as any);
        scrollingContainer.addEventListener("touchcancel", this.handleTouchEnd, { passive: true } as any);

        this.clickId = target.getAttribute("data-id");
        this.clickGroupId = target.getAttribute("data-group-index");
        this.mouseClickTimeout = setTimeout(() => this.handleIsPress(e, target), 250);
    };

    private handleIsPress = (e, target) => {
        const { allItems, snapshotItems, scrollingContainer, onGetCellDisplayInfo, containerHeight } = this.props;
        const { pageX, pageY } = e;

        this.mouseClickTimeout = null;
        // Make the cursor dragging everywhere
        document.body.style.cursor = "-webkit-grabbing";
        const itemsArr = snapshotItems ? snapshotItems : allItems;
        const index = itemsArr.findIndex((item) => {
            return amphibiousGetIn(item, Item.FIELDS.ID) === this.clickId;
        });
        const draggingItem = amphibiousGet(itemsArr, index);
        const itemsWithoutDraggingItem = itemsArr.remove(index);

        this.draggingBoundingRect = target.getBoundingClientRect();
        this.containerBoundingRect = scrollingContainer.getBoundingClientRect();

        const elementOffset = [pageX - this.draggingBoundingRect.left, pageY - this.draggingBoundingRect.top];

        let scrollTriggerDistance = containerHeight * 0.2;
        const halfHeight = this.draggingBoundingRect.height / 2;
        if (scrollTriggerDistance > halfHeight) {
            scrollTriggerDistance = halfHeight;
        } else if (scrollTriggerDistance < 30) {
            scrollTriggerDistance = 30;
        }

        this.setState({
            isDragging: true,
            draggingIndex: index,
            draggingId: this.clickId,
            draggingGroupId: this.clickGroupId,
            draggingItem,
            itemsWithoutDraggingItem,
            dimensions: result(onGetCellDisplayInfo, { data: draggingItem }),
            elementOffset,
            mousePositionOnPage: [pageX, pageY],
            mousePositionInScrollContainer: [
                pageX - this.containerBoundingRect.left + scrollingContainer.scrollLeft,
                pageY - this.containerBoundingRect.top + scrollingContainer.scrollTop + this.props.visualOffset,
            ],
            scrollTriggerDistance,
            scrollTriggerFastDistance: scrollTriggerDistance / 2,
        });

        // Set initial style
        this.updateDraggingElementStyle(
            true,
            pageX - elementOffset[0],
            this.props.visualOffset > 0
                ? pageY + scrollingContainer.scrollTop - this.props.visualOffset
                : pageY - elementOffset[1],
        );

        scrollingContainer.addEventListener("touchmove", this.handleTouchMove, { passive: true } as any);
        scrollingContainer.addEventListener("mousemove", (this as any).handleMouseMove, { passive: true } as any);
    };

    private handleTouchMove = (e) => {
        const touch = e.touches[0]; // this.getFirstActiveTouch(e.touches);
        this.handleCommonMove(touch);
    };

    private handleMouseMove = (e: React.MouseEvent<any>) => {
        this.handleCommonMove(e);
    };

    private handleCommonMove = ({ pageX, pageY, clientX, clientY }: React.MouseEvent<any> | Touch) => {
        const { scrollingContainer } = this.props;
        const { elementOffset } = this.state;

        this.throttledMouseMoveUpdate({
            mousePositionOnPage: [pageX, pageY],
            mousePositionInScrollContainer: [
                pageX - this.containerBoundingRect.left + scrollingContainer.scrollLeft,
                pageY - this.containerBoundingRect.top + scrollingContainer.scrollTop + this.props.visualOffset,
            ],
        });
        this.updateDraggingElementStyle(
            true,
            pageX - elementOffset[0],
            this.props.visualOffset > 0
                ? pageY + scrollingContainer.scrollTop - this.props.visualOffset
                : pageY - elementOffset[1],
        );
    };

    // tslint:disable-next-line
    private throttledMouseMoveUpdate = throttle((newState) => {
        this.setState(newState);
    }, 150);

    private handleTouchEnd = (e) => {
        this.handleCommonEnd(e.currentTarget);
    };

    private handleMouseUp = (e) => {
        this.handleCommonEnd(e.currentTarget);
    };

    private handleCommonEnd = (target) => {
        // Reset the cursor dragging to auto
        document.body.style.cursor = "auto";

        const { scrollingContainer } = this.props;
        this.throttledMouseMoveUpdate.cancel();

        scrollingContainer.removeEventListener("mouseup", this.handleMouseUp);
        scrollingContainer.removeEventListener("touchend", this.handleTouchEnd);
        scrollingContainer.removeEventListener("touchcancel", this.handleTouchEnd);
        if (this.mouseClickTimeout || this.mouseClickTimeout === 0) {
            clearTimeout(this.mouseClickTimeout);

            this.mouseClickTimeout = null;
            if (this.props.onClick && this.clickId) {
                this.props.onClick(this.clickId);
            }
            return;
        }
        this.mouseClickTimeout = null;
        scrollingContainer.removeEventListener("mousemove", (this as any).handleMouseMove);
        scrollingContainer.removeEventListener("touchmove", this.handleTouchMove);

        if (this.props.onDragEnd && this.currentDraggingDestination !== undefined) {
            const destinationIndex = this.currentDraggingDestination;
            this.props.onDragEnd({
                currentIndex: this.state.draggingIndex,
                destinationIndex,
                rowId: target.getAttribute("data-id"),
            });
        }

        this.updateDraggingElementStyle(false);

        this.setState({
            isDragging: false,
            draggingId: undefined,
            draggingGroupId: undefined,
            draggingIndex: undefined,
            draggingItem: undefined,
            itemsWithoutDraggingItem: undefined,
            mousePositionOnPage: undefined,
            mousePositionInScrollContainer: undefined,
            dimensions: undefined,
        });
    };

    // private getFirstActiveTouch (touchList: TouchList): Touch {
    //     // tslint:disable-next-line
    //     for (let i = 0; i< touchList.length; i++) {
    //         const matches = touchList[i].identifier === this.touchStart.identifier;
    //         if (matches) {
    //             return touchList[i];
    //         }
    //     }

    //     return null;
    // }

    private updateDraggingElementStyle(isDragging, left = 0, top = 0) {
        this.draggingElement.style.display = isDragging ? "block" : "none";
        this.draggingElement.style.webkitTransform = `translate3d(${left}px, ${top}px, 0px)`;
        this.draggingElement.style.transform = `translate3d(${left}px, ${top}px, 0px)`;
    }

    private shouldCancelStart = (e) => {
        // Cancel sorting if the event target is an `input`, `textarea`, `select` or `option`
        const disabledElements = ["input", "textarea", "select", "option", "button"];

        if (disabledElements.indexOf(e.target.tagName.toLowerCase()) !== -1) {
            return true; // Return true to cancel sorting
        }
    };
}
